/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useCallback, useEffect, useRef } from "react";

import { useUnityDispatch, useUnitySelector } from "../../../viewModel";
import {
  selectCurrentUrl,
  selectPreviousNavUrl
} from "../../../viewModel/screens";
import {
  clearBrowserPictureInPictureMode,
  closeBrowserPictureInPictureMode,
  enterBrowserPictureInPictureMode,
  exitBrowserPictureInPictureMode,
  getPictureInPictureLeaveResolveTimeoutMs,
  getPlaybackVideoElement,
  isPictureInPictureSupported,
  isPipEntryPending,
  isPipToGuideConversionActive,
  isReturnToPlayerIntended,
  registerBeforeNavigateHandler,
  selectWasPiGEnteredFromMiniClick,
  setPipEntryPending,
  setPipToGuideConversionActive,
  setReturnToPlayerIntended,
  setWasPiGEnteredFromMiniClick,
  shouldReturnToPlayer,
  unregisterBeforeNavigateHandler
} from "../../../viewModel/videoPlayerUi";
import { useNavigationService } from "../../hooks/useNavigationService";
import { DeviceModel } from "../../../model/device";
import { LocationUrls } from "../../../model/navigation";
import { Log } from "../../../model/logging";

const TAG: string = "usePictureInPictureCoordinator";

const FULLSCREEN_CHANGE_EVENTS: ReadonlyArray<string> = [
  "fullscreenchange",
  "webkitfullscreenchange",
  "mozfullscreenchange",
  "MSFullscreenChange"
];

/**
 * usePictureInPictureCoordinator — persistent PiP routing side-effect hook.
 *
 * Single owner of the native browser PiP DOM events. Invoked from MainLayout so
 * it survives navigation between /player and all other authenticated screens.
 *
 *  - enterpictureinpicture → navigate back to the screen playback was started from
 *    (previousNavUrl). Falls back to HOME_SCREEN when previousNavUrl is an auth
 *    route (SPLASH, SIGN_IN) or unknown.
 *  - leavepictureinpicture → heuristically resolve whether the user returned to
 *    the tab (→ /player) or closed the PiP window (→ stop playback).
 *  - PiG ⇄ PiP bridge → seamlessly switch between Guide PiG and browser PiP as
 *    the user navigates while a mini-player session is active.
 *
 * No-op on SmartTV devices, where native browser PiP is unsupported.
 */
export function usePictureInPictureCoordinator(): void {
  const isBrowserMode: boolean = !DeviceModel.isTvDevice();
  const dispatch = useUnityDispatch();
  const { navigateTo } = useNavigationService();
  const currentUrl: string = useUnitySelector(selectCurrentUrl);
  const previousNavUrl: string | null = useUnitySelector(selectPreviousNavUrl);
  const wasPiGEnteredFromMiniClick: boolean = useUnitySelector(
    selectWasPiGEnteredFromMiniClick
  );

  const previousCurrentUrlRef = useRef<string>(currentUrl);
  const wasPiGEnteredFromMiniClickRef = useRef<boolean>(
    wasPiGEnteredFromMiniClick
  );
  const currentUrlRef = useRef<string>(currentUrl);
  const previousNavUrlRef = useRef<string | null>(previousNavUrl);
  const pigToPipHandledRef = useRef<boolean>(false);
  const activeLeaveResolutionCleanupRef = useRef<(() => void) | null>(null);

  // Keep latest route + flags in refs so the stable DOM event handlers read
  // fresh values without re-registering listeners on every navigation change.
  useEffect((): void => {
    currentUrlRef.current = currentUrl;
  }, [currentUrl]);

  useEffect((): void => {
    previousNavUrlRef.current = previousNavUrl;
  }, [previousNavUrl]);

  useEffect((): void => {
    wasPiGEnteredFromMiniClickRef.current = wasPiGEnteredFromMiniClick;
  }, [wasPiGEnteredFromMiniClick]);

  // Common guard + lookup for converting a Guide PiG session into browser
  // PiP: verifies PiP is supported, no PiP session is already active, and the
  // shared playback <video> element is available. Returns null (after logging
  // a warning) when any guard fails, so callers can bail out early.
  const resolvePipEntryVideoElement = (): HTMLVideoElement | null => {
    if (
      !isPictureInPictureSupported() ||
      document.pictureInPictureElement !== null ||
      isPipEntryPending()
    ) {
      return null;
    }

    const videoEl: HTMLVideoElement | null = getPlaybackVideoElement();
    if (videoEl === null) {
      Log.w(TAG, "PiG->PiP skipped: playback video element unavailable");
      return null;
    }

    return videoEl;
  };

  const handleBeforeNavigate = useCallback((targetUrl: string | null): void => {
    // Resolve the destination: forward navigations pass the target directly;
    // back navigations resolve it from the navigation stack.
    const resolvedTarget: string | null =
      targetUrl === null ? previousNavUrlRef.current : targetUrl;

    // Only convert Guide PiG into browser PiP while a mini-click PiG session
    // is active and the user is leaving Guide for a non-player screen.
    if (
      currentUrlRef.current !== LocationUrls.GUIDE ||
      !wasPiGEnteredFromMiniClickRef.current ||
      resolvedTarget === LocationUrls.GUIDE ||
      resolvedTarget === LocationUrls.PLAYER
    ) {
      return;
    }

    const videoEl: HTMLVideoElement | null = resolvePipEntryVideoElement();
    if (videoEl === null) {
      return;
    }

    // Invoked synchronously inside the navigation gesture so the browser
    // honours the transient user activation requestPictureInPicture needs.
    setReturnToPlayerIntended(true);
    pigToPipHandledRef.current = true;
    setPipEntryPending(true);
    videoEl.requestPictureInPicture().catch((error: unknown): void => {
      pigToPipHandledRef.current = false;
      setPipEntryPending(false);
      Log.e(TAG, "PiG->PiP transition failed", error);
    });
  }, []);

  useEffect((): (() => void) | void => {
    if (!isBrowserMode) {
      return undefined;
    }

    registerBeforeNavigateHandler(handleBeforeNavigate);
    return (): void => {
      unregisterBeforeNavigateHandler(handleBeforeNavigate);
    };
  }, [handleBeforeNavigate, isBrowserMode]);

  const handleEnterPictureInPicture = useCallback((): void => {
    // Single owner of the enter side-effect: switch the player to PiP mode and
    // snapshot the playing state so playback can be restored on return.
    dispatch(enterBrowserPictureInPictureMode());

    setReturnToPlayerIntended(
      currentUrlRef.current === LocationUrls.PLAYER ||
        wasPiGEnteredFromMiniClickRef.current
    );

    // Route redirect is only required when PiP is entered from /player.
    if (currentUrlRef.current !== LocationUrls.PLAYER) {
      return;
    }

    const previousUrl: string | null = previousNavUrlRef.current;

    // Navigate away from /player to the playback-origin screen. Fall back to
    // HOME when the previous route is auth-only or unavailable.
    if (
      previousUrl !== null &&
      previousUrl !== LocationUrls.SPLASH &&
      previousUrl !== LocationUrls.SIGN_IN &&
      previousUrl !== LocationUrls.PLAYER
    ) {
      navigateTo(previousUrl);
      return;
    }

    navigateTo(LocationUrls.HOME_SCREEN);
  }, [dispatch, navigateTo]);

  const handleLeavePictureInPicture = useCallback((): void => {
    // Clear any prior in-flight leave-resolution (listeners + timeout) before
    // starting a new one — guards against rapid leave/enter/leave toggling
    // leaking duplicate listeners or timers.
    if (activeLeaveResolutionCleanupRef.current !== null) {
      activeLeaveResolutionCleanupRef.current();
      activeLeaveResolutionCleanupRef.current = null;
    }

    // PiP→Guide conversion suppresses the return-to-player path.
    if (isPipToGuideConversionActive()) {
      setPipToGuideConversionActive(false);
      dispatch(clearBrowserPictureInPictureMode());
      return;
    }

    const removeFullscreenListeners = (handler: () => void): void => {
      FULLSCREEN_CHANGE_EVENTS.forEach((eventName: string): void => {
        document.removeEventListener(eventName, handler);
      });
    };

    const resolveReturnToPlayer = (): void => {
      setReturnToPlayerIntended(false);
      dispatch(exitBrowserPictureInPictureMode());
      navigateTo(LocationUrls.PLAYER);
    };

    const handleFullscreenChange = (): void => {
      if (shouldReturnToPlayer(isReturnToPlayerIntended())) {
        removeFullscreenListeners(handleFullscreenChange);
        activeLeaveResolutionCleanupRef.current = null;
        resolveReturnToPlayer();
      }
    };

    FULLSCREEN_CHANGE_EVENTS.forEach((eventName: string): void => {
      document.addEventListener(eventName, handleFullscreenChange);
    });

    // Fallback: if no fullscreen transition resolves the intent in time, decide
    // between returning to the player and closing playback.
    const timeoutId: ReturnType<typeof setTimeout> = setTimeout((): void => {
      removeFullscreenListeners(handleFullscreenChange);
      activeLeaveResolutionCleanupRef.current = null;

      if (shouldReturnToPlayer(isReturnToPlayerIntended())) {
        resolveReturnToPlayer();
        return;
      }

      setReturnToPlayerIntended(false);
      dispatch(closeBrowserPictureInPictureMode());
    }, getPictureInPictureLeaveResolveTimeoutMs());

    activeLeaveResolutionCleanupRef.current = (): void => {
      removeFullscreenListeners(handleFullscreenChange);
      clearTimeout(timeoutId);
    };
  }, [dispatch, navigateTo]);

  useEffect((): (() => void) | void => {
    // PiP is a browser-only feature; skip on SmartTV platforms.
    if (!isBrowserMode || !isPictureInPictureSupported()) {
      return undefined;
    }

    const videoEl: HTMLVideoElement | null = getPlaybackVideoElement();
    if (videoEl === null) {
      return undefined;
    }

    videoEl.addEventListener(
      "enterpictureinpicture",
      handleEnterPictureInPicture
    );
    videoEl.addEventListener(
      "leavepictureinpicture",
      handleLeavePictureInPicture
    );

    return (): void => {
      videoEl.removeEventListener(
        "enterpictureinpicture",
        handleEnterPictureInPicture
      );
      videoEl.removeEventListener(
        "leavepictureinpicture",
        handleLeavePictureInPicture
      );
      // Guard against an unmount while a leave-resolution (fullscreenchange
      // listeners + fallback timeout) is still in flight.
      if (activeLeaveResolutionCleanupRef.current !== null) {
        activeLeaveResolutionCleanupRef.current();
        activeLeaveResolutionCleanupRef.current = null;
      }
    };
  }, [handleEnterPictureInPicture, handleLeavePictureInPicture, isBrowserMode]);

  // Persistent PiG ⇄ PiP bridge:
  // - Player mini click → Guide PiG keeps a one-shot flag alive across unmount.
  // - Any active browser PiP → Guide converts PiP back into Guide PiG.
  // - Guide → non-player route converts PiG into browser PiP.
  useEffect((): void => {
    if (!isBrowserMode) {
      return;
    }

    const previousCurrentUrl: string = previousCurrentUrlRef.current;

    // Consume the one-shot intent once the user returns to full-screen playback.
    if (
      wasPiGEnteredFromMiniClick &&
      previousCurrentUrl !== LocationUrls.PLAYER &&
      currentUrl === LocationUrls.PLAYER
    ) {
      dispatch(setWasPiGEnteredFromMiniClick(false));
      previousCurrentUrlRef.current = currentUrl;
      return;
    }

    // PiP → Guide: exit browser PiP so GuideScreen can take over with PiG.
    if (
      currentUrl === LocationUrls.GUIDE &&
      previousCurrentUrl !== LocationUrls.GUIDE &&
      document.pictureInPictureElement !== null
    ) {
      const exitPipForGuide = async (): Promise<void> => {
        try {
          // Preserve Guide→next-route PiP re-entry intent after conversion.
          dispatch(setWasPiGEnteredFromMiniClick(true));
          setPipToGuideConversionActive(true);
          await document.exitPictureInPicture();
        } catch (error: unknown) {
          setPipToGuideConversionActive(false);
          Log.e(TAG, "PiP->PiG transition failed", error);
        }
      };

      void exitPipForGuide();
    }

    // Guide → non-player route: convert Guide PiG into browser PiP.
    const shouldEnterPip: boolean =
      wasPiGEnteredFromMiniClick &&
      previousCurrentUrl === LocationUrls.GUIDE &&
      currentUrl !== LocationUrls.GUIDE &&
      currentUrl !== LocationUrls.PLAYER;

    if (!shouldEnterPip) {
      previousCurrentUrlRef.current = currentUrl;
      return;
    }

    // The navigation gesture already converted PiG into PiP synchronously; the
    // reactive path only runs as a fallback for non-gesture route changes.
    if (pigToPipHandledRef.current) {
      pigToPipHandledRef.current = false;
      previousCurrentUrlRef.current = currentUrl;
      return;
    }

    const enterPip = async (): Promise<void> => {
      try {
        const videoEl: HTMLVideoElement | null = resolvePipEntryVideoElement();
        if (videoEl === null) {
          return;
        }
        setReturnToPlayerIntended(true);
        setPipEntryPending(true);
        await videoEl.requestPictureInPicture();
      } catch (error: unknown) {
        setPipEntryPending(false);
        Log.e(TAG, "PiG->PiP transition failed", error);
      }
    };

    void enterPip();
    previousCurrentUrlRef.current = currentUrl;
  }, [currentUrl, wasPiGEnteredFromMiniClick, dispatch, isBrowserMode]);
}
